import React from 'react';
import { Box, Tooltip } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import ComputerIcon from '@mui/icons-material/Computer';
import { alpha, useTheme } from '@mui/material/styles';

interface Entity {
  id?: string;
  name?: string;
  identifier?: string; // email address or phone number
  role: string;
  tag?: string | null; // can contain 'twilio' for SMS
  valid_phone_user?: boolean;
  valid_email_user?: boolean;
}

interface ContactMediumIconsProps {
  participatingEntities?: Entity[];
  /**
   * Size in px for the icon. Defaults to 16.
   */
  size?: number;
}

/**
 * Displays up to three unique contact medium icons (e.g. email, sms) derived from conversation entities.
 * Each icon is wrapped in a tooltip that lists the concrete identifiers being used for that medium.
 * The component is memoised so that it only re-renders when the `participatingEntities` list actually changes.
 */
const ContactMediumIconsComponent: React.FC<ContactMediumIconsProps> = ({ participatingEntities = [], size = 16 }) => {
  const theme = useTheme();

  type MediumItem = {
    key: 'email' | 'sms' | 'chat';
    tooltip: string;
    icon: React.ReactElement;
  };

  const mediums: MediumItem[] = React.useMemo(() => {
    if (!participatingEntities || participatingEntities.length === 0) return [];

    // Check if this is minimal payload (array of strings) or full payload (array of objects)
    const isMinimalPayload = participatingEntities.length > 0 && typeof participatingEntities[0] === 'string';
    
    if (isMinimalPayload) {
      // For minimal payload, we don't have enough info to determine contact mediums
      // Return an empty array to not show any icons
      return [];
    }

    const items: MediumItem[] = [];
    const seenMediums = new Set<string>();

    // Filter to only user entities (exclude assistant and function roles)
    const userEntities = participatingEntities.filter(e => e.role === 'user');

    // EMAIL medium ------------------------------------------------------
    const emailEntities = userEntities.filter(e => {
      // Check if identifier contains @ (email address)
      return e.identifier && e.identifier.includes('@') && !e.identifier.includes('sms:') && !e.identifier.includes('phone:');
    });
    if (emailEntities.length > 0 && !seenMediums.has('email')) {
      seenMediums.add('email');
      const label = emailEntities.length === 1 ? 'Email contact' : 'Email contacts';
      const identifiers = emailEntities.map(e => e.identifier || 'Unknown').join(', ');
      items.push({
        key: 'email',
        tooltip: `${label}: ${identifiers}`,
        icon: <EmailIcon sx={{ fontSize: size }} />
      });
    }

    // SMS medium --------------------------------------------------------
    const smsEntities = userEntities.filter(e => {
      if (!e.identifier) return false;
      // Check if identifier is a phone number (starts with +) or contains sms:/phone: prefix
      return e.identifier.startsWith('+') || 
             e.identifier.includes('sms:') || 
             e.identifier.includes('phone:') ||
             e.tag === 'twilio';
    });
    if (smsEntities.length > 0 && !seenMediums.has('sms')) {
      seenMediums.add('sms');
      const label = smsEntities.length === 1 ? 'SMS contact' : 'SMS contacts';
      const identifiers = smsEntities
        .map(e => {
          const name = e.name || 'Unknown';
          const id = e.identifier || 'Unknown';
          // Extract phone number if it has a prefix
          const phone = id.replace(/^(sms:|phone:)/, '');
          return `${name} (${phone})`;
        })
        .join(', ');
      items.push({
        key: 'sms',
        tooltip: `${label}: ${identifiers}`,
        icon: <SmsIcon sx={{ fontSize: size }} />
      });
    }

    // CHAT medium -------------------------------------------------------
    const chatEntities = userEntities.filter(e => {
      if (!e.identifier) return false;
      // Check if identifier contains ui_chat: or live_chat: prefix
      return e.identifier.includes('ui_chat:') || 
             e.identifier.includes('live_chat:') ||
             e.tag === 'chat';
    });
    if (chatEntities.length > 0 && !seenMediums.has('chat')) {
      seenMediums.add('chat');
      const label = chatEntities.length === 1 ? 'Web chat' : 'Web chats';
      const names = chatEntities
        .map(e => e.name || 'Unknown')
        .filter(name => name !== 'Unknown')
        .join(', ');
      items.push({
        key: 'chat',
        tooltip: names ? `${label}: ${names}` : label,
        icon: <ComputerIcon sx={{ fontSize: size }} />
      });
    }

    return items.slice(0, 3); // cap at 3 mediums, per requirement
  }, [participatingEntities, size]);

  if (mediums.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        px: 0.4,
        py: 0.2,
        bgcolor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.black, 0.4)
          : alpha(theme.palette.common.white, 0.6),
        borderRadius: '8px',
        boxShadow: 1,
      }}
    >
      {mediums.map(m => (
        <Tooltip key={m.key} title={m.tooltip} arrow>
          <Box component="span" sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary'
            }
          }}>
            {m.icon}
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
};

// Memoise to avoid unnecessary re-renders
const ContactMediumIcons = React.memo(ContactMediumIconsComponent);
ContactMediumIcons.displayName = 'ContactMediumIcons';

export default ContactMediumIcons; 
